<template>
	<div class="pa-3 new-back full-height">

		<filter-search-input
			v-model="filters.search" :applied-filters="appliedFilters"
			@input="searchChanged"
			outlined dense
		>
			<template #append>
        <div v-if="foreignCurrencies.length" class="d-flex align-center flex-wrap my-3 px-1" style="gap: 16px;">
          <div v-for="fc in foreignCurrencies" :key="fc" class="d-flex align-center" style="gap: 8px;">
            <div class="exchange-box d-flex align-center px-3 rounded-lg">
              <span class="body-2 font-weight-medium">$1</span>
              <span class="ml-2" style="font-size: 18px; line-height: 1;">{{ currencyFlags[fc] }}</span>
              <span class="ml-1 body-2 font-weight-medium">{{ fc }}</span>
            </div>
            <v-icon small color="grey darken-1">mdi-swap-horizontal</v-icon>
            <div class="exchange-box d-flex align-center px-2 rounded-lg">
              <span class="body-2 font-weight-medium mr-1">$</span>
              <input
                type="number"
                :value="exchangeRates[fc]"
                @input="updateExchangeRate(fc, $event.target.value)"
                class="exchange-input body-2 font-weight-medium"
                step="0.01" min="0"
              />
              <span class="ml-1" style="font-size: 18px; line-height: 1;">🇲🇽</span>
              <span class="ml-1 body-2 font-weight-medium">MXN</span>
            </div>
          </div>
        </div>
			</template>

		<v-autocomplete
			v-model="filters.customers" :items="customers"
			@change="fetchRows"
			label="Customer"
				dense outlined hide-details clearable multiple
				item-text="name" item-value="id"
			/>

			<v-select
				v-model="displayCurrency" :items="currencies"
				label="Display Currency"
				dense outlined hide-details
				item-text="text" item-value="text"
			>
				<template #selection="{ item }">
					<span style="font-size: 16px; line-height: 1;" class="mr-1">{{ currencyFlags[item.text] }}</span>
					<span>{{ item.text }}</span>
				</template>
				<template #item="{ item }">
					<span style="font-size: 16px; line-height: 1;" class="mr-2">{{ currencyFlags[item.text] }}</span>
					<span>{{ item.text }}</span>
				</template>
			</v-select>
		</filter-search-input>


	<v-simple-table v-if="rows.length" class="rounded-lg bordered new-back lighten2">
		<thead>
			<tr>
				<th class="text-left">Customer</th>
				<th class="text-right">Present Current Due</th>
				<th class="text-right">1-30 Days Past Due</th>
				<th class="text-right">31-60 Days Past Due</th>
				<th class="text-right">61-90 Days Past Due</th>
				<th class="text-right">91+ Days Past Due</th>
				<th class="text-right">Total Amount Due</th>
			</tr>
		</thead>
		<tbody>
			<tr
				v-for="row in rows"
					:key="row.customerId"
					:class="{ 'cursor-pointer': permissions.detail }"
				>
					<td>{{ row.customerName }}</td>
					<template v-if="row.missingRates.length">
						<td colspan="6" class="text-center">
							<v-icon small color="orange darken-2" class="mr-1">mdi-alert-circle-outline</v-icon>
							<span class="orange--text text--darken-2 caption">
								Por favor asigne un tipo de cambio para: {{ row.missingRates.join(', ') }}
							</span>
						</td>
					</template>
				<template v-else>
					<td v-for="(col, ci) in [
						{ amount: row.presentCurrentDue, items: row.items.present },
						{ amount: row.days1_30,          items: row.items.d1_30   },
						{ amount: row.days31_60,         items: row.items.d31_60  },
						{ amount: row.days61_90,         items: row.items.d61_90  },
						{ amount: row.days91Plus,        items: row.items.d91Plus },
						{ amount: row.totalAmountDue,    items: row.items.total, bold: true },
					]" :key="ci" class="text-right" :class="{ 'foreign-currency-amount': row.hasForeignCurrency, 'font-weight-bold': col.bold }">
						<v-tooltip v-if="row.hasForeignCurrency && col.items.length" :bottom="ci === 5 ? false : true" :left="ci === 5 ? true : false" :max-width="400" color="grey darken-4">
							<template #activator="{ on }">
								<span v-on="on" class="tooltip-trigger">{{ formatAmount(col.amount, displayCurrency) }}</span>
							</template>
							<div class="py-1">
								<div v-for="item in col.items" :key="item.id" class="tooltip-item mb-1">
									<span>{{ item.invoiceCode }}: </span>
									<span>{{ formatAmount(item.originalAmount, item.originalCurrency) }} <strong>{{ item.originalCurrency }}</strong></span>
									<span v-if="item.isForeign" class="grey--text text--lighten-1"> → {{ formatAmount(item.convertedAmount, displayCurrency) }} {{ displayCurrency }}</span>
								</div>
							</div>
						</v-tooltip>
						<span v-else>{{ formatAmount(col.amount, displayCurrency) }}</span>
					</td>
				</template>
				</tr>
				<tr v-if="rows.length" class="totals-row font-weight-bold">
					<td>TOTALS:</td>
					<td class="text-right">{{ formatAmount(totalsRow.presentCurrentDue, displayCurrency) }}</td>
					<td class="text-right">{{ formatAmount(totalsRow.days1_30, displayCurrency) }}</td>
					<td class="text-right">{{ formatAmount(totalsRow.days31_60, displayCurrency) }}</td>
					<td class="text-right">{{ formatAmount(totalsRow.days61_90, displayCurrency) }}</td>
					<td class="text-right">{{ formatAmount(totalsRow.days91Plus, displayCurrency) }}</td>
					<td class="text-right">{{ formatAmount(totalsRow.totalAmountDue, displayCurrency) }}</td>
				</tr>
			</tbody>
		</v-simple-table>
		<v-alert v-else type="info" outlined class="ma-4">No outstanding invoices match the current filters.</v-alert>
	</div>
</template>

<script>
import moment from "moment"
import ExcelBtn from "../../components/ExcelBtn.vue"

export default {
	props: ['customerId', 'invoiceIds', 'paid', 'currency'],
	components: { ExcelBtn },
	data: () => ({
		customers: [],
		rows: [],
		foreignCurrencies: [],
		invoicesFilter: [],
		ratesTimeout: null,
		filters: {
			invoices: [],
			customers: [],
			currencies: [],
			statuses: [],
		},
	currencies: [
		{text: 'MXN'},
		{text: 'USD'},
		{text: 'EUR'},
	],
	displayCurrency: 'USD',
	exchangeRates: {},
	currencyFlags: {
		USD: '🇺🇸',
		MXN: '🇲🇽',
		EUR: '🇪🇺',
	},
		countries: [
			{ name: 'Mexico', value: 0 },
			{ name: 'USA', 		value: 1 },
		],
		statuses: [
			{ text: 'Pending', value: 1 },
			{ text: 'Paid', value: 2 },
			{ text: 'Cancelled', value: 3 },
			{ text: 'Partial', value: 4 },
			{ text: 'Draft', value: 0 },
		],

		pagination: {
			page: 1,
			itemsPerPage: 10,
			totalItems: 0,
			sortBy: '',
			sortDesc: false,
		},
		searchTimeout: null,

		permissions: {
			detail: false,
			add: false,
			actions: false,
		},
	}),
	computed: {
		appliedFilters(){
			return this.countActiveFilters(this.filters, ['search'])
		},
		totalsRow() {
			return this.rows.reduce((acc, r) => ({
				presentCurrentDue: acc.presentCurrentDue + r.presentCurrentDue,
				days1_30:          acc.days1_30          + r.days1_30,
				days31_60:         acc.days31_60         + r.days31_60,
				days61_90:         acc.days61_90         + r.days61_90,
				days91Plus:        acc.days91Plus        + r.days91Plus,
				totalAmountDue:    acc.totalAmountDue    + r.totalAmountDue,
			}), { presentCurrentDue: 0, days1_30: 0, days31_60: 0, days61_90: 0, days91Plus: 0, totalAmountDue: 0 })
		},
	},
	watch: {
		displayCurrency() {
			this.fetchRows()
		},
	},
	mounted() {
		document.title = 'Outstanding Invoices'

		this.$security.hasRequiredPermission('detail')
			.then(res => this.permissions.detail = res)
		this.$security.hasRequiredPermission('add')
			.then(res => this.permissions.add = res)
		this.$security.hasRequiredPermission('full_access')
			.then(res => this.permissions.actions = res)

    this.$q.log(4, 26, 3, 'NA')
		this.setUserPreferences("Invoices", "filters")
		if(this.customerId){
			this.filters = {
				invoices: [],
				customers: [],
				customerCountries: [],
				currencies: [],
				statuses: [1, 2, 4],
			}
			this.filters.customers.push(this.customerId)

			if(this.paid == 'false'){
				this.filters.statuses = [1, 4]
			}
			if(this.currency){
				this.displayCurrency = this.currency
			}
		}

		if(this.invoiceIds) {
			const invoiceIdsArray = Array.isArray(this.invoiceIds) ? this.invoiceIds : this.invoiceIds.split(',')
			if(invoiceIdsArray.length) {
				this.filters = {
					invoices: [],
					customers: [],
					customerCountries: [],
					currencies: [],
					statuses: [1, 2, 4],
				}
				this.filters.invoices = invoiceIdsArray

				if(this.paid == 'false'){
					this.filters.statuses = [1, 4]
				}
				if(this.currency){
					this.displayCurrency = this.currency
				}
			}
		}

		this.init()
	},
	methods: {
		init() {
			this.$http.get("/api/customer?onlyWithCustomerProjects=true")
				.then(res => {
					console.log("🚀 ~ res.data:", res.data)
					this.customers = _.orderBy(res.data, ['name'])
				})
				.catch(err => {
					console.error(err)
					this.$root.error("Cannot obtain customers.")
				})
			this.fetchRows()
		},
		fetchRows() {
			this.$http.post('api/invoice/list/outstanding', {
				search:            this.filters.search,
				customers:         this.filters.customers,
				displayCurrency:   this.displayCurrency,
				exchangeRates:     this.exchangeRates,
			})
				.then(res => {
					this.rows = res.data.rows || []
					const incoming = res.data.foreignCurrencies || []
					incoming.forEach(c => {
						if (this.exchangeRates[c] === undefined) {
							this.$set(this.exchangeRates, c, 0)
						}
					})
					this.foreignCurrencies = incoming
				})
				.catch(err => {
					console.error(err)
					this.$root.error("Cannot obtain invoices.")
				})
		},
		searchChanged() {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(this.fetchRows, 500)
		},
		updateExchangeRate(currency, value) {
			this.$set(this.exchangeRates, currency, parseFloat(value) || 0)
			clearTimeout(this.ratesTimeout)
			this.ratesTimeout = setTimeout(this.fetchRows, 600)
		},
		formatAmount(amount, currency) {
			if (amount == null || Math.abs(amount) < 0.01) return '-'
			const symbols = { USD: '$', MXN: '$', EUR: '€' }
			const sym = currency ? (symbols[currency] || '') : ''
			return `${sym}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
		},
	},
}
</script>

<style lang="scss" scoped>
.totals-row {
	background-color: rgba(0, 0, 0, 0.05);
}
.foreign-currency-amount {
	color: #F59E0B;
	font-weight: 500;
}
.tooltip-trigger {
	cursor: help;
	border-bottom: 1px dashed #F59E0B;
}
.cursor-pointer:hover {
	background-color: rgba(0, 0, 0, 0.02);
}
.exchange-box {
	border: 1px solid #e0e0e0;
	height: 40px;
	background: white;
}
.exchange-input {
	width: 80px;
	outline: none;
	border: none;
	background: transparent;
}
.exchange-input::-webkit-outer-spin-button,
.exchange-input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
</style>